import ScimToken from 'App/babel/resources/ScimToken';
import ClientParam from 'App/resources/ClientParam';
import AppService from 'App/legacy/AppService';
import NotificationService from 'App/babel/NotificationService';
import logError from 'Helpers/logError';

/** Client param 338. The role every SCIM-provisioned user is given. */
const SCIM_DEFAULT_ROLE_PARAM = 338;

export type ScimProvisioningState = {
	loading: boolean;
	saving: boolean;
	/** SCIM is on precisely when a credential exists. Nothing else records it. */
	enabled: boolean;
	baseUrl: string;
	defaultRoleId: number;
	/**
	 * Held only until the admin navigates away. The server returns the plaintext
	 * once, at mint time, so there is nowhere to fetch it back from.
	 */
	mintedKey: string | null;
};

export const initialState: ScimProvisioningState = {
	loading: false,
	saving: false,
	enabled: false,
	baseUrl: '',
	defaultRoleId: 0,
	mintedKey: null
};

const LOAD = '[SCIM] LOAD';
const LOAD_SUCCESS = '[SCIM] LOAD_SUCCESS';
const LOAD_ERROR = '[SCIM] LOAD_ERROR';
const SET_DEFAULT_ROLE = '[SCIM] SET_DEFAULT_ROLE';
const SAVING = '[SCIM] SAVING';
const MINTED = '[SCIM] MINTED';
const REVOKED = '[SCIM] REVOKED';
const DISMISS_KEY = '[SCIM] DISMISS_KEY';
const SAVE_FAILED = '[SCIM] SAVE_FAILED';

const reducer = (state = initialState, action: any): ScimProvisioningState => {
	const { type, ...rest } = action;
	switch (type) {
		case LOAD:
			return { ...state, loading: true };
		case LOAD_SUCCESS:
			return { ...state, loading: false, ...rest };
		case LOAD_ERROR:
			return { ...state, loading: false };
		case SET_DEFAULT_ROLE:
			return { ...state, defaultRoleId: rest.defaultRoleId };
		case SAVING:
			return { ...state, saving: true };
		case MINTED:
			return { ...state, saving: false, enabled: true, mintedKey: rest.key, baseUrl: rest.baseUrl };
		case REVOKED:
			return { ...state, saving: false, enabled: false, mintedKey: null };
		case DISMISS_KEY:
			return { ...state, mintedKey: null };
		// Clears the spinner without touching `enabled`. Dispatching REVOKED here
		// would claim SCIM is off while a live credential still authenticates, and
		// would hide the Revoke button that turns it off for real.
		case SAVE_FAILED:
			return { ...state, saving: false, defaultRoleId: rest.defaultRoleId ?? state.defaultRoleId };
		default:
			return state;
	}
};

export default reducer;

export const load = () => async (dispatch: any) => {
	dispatch({ type: LOAD });
	try {
		const state = await ScimToken.getState();
		const defaultRoleId = Number(AppService.getMetadata().params.ScimDefaultRoleId || 0);
		dispatch({ type: LOAD_SUCCESS, ...state, defaultRoleId });
	} catch (e) {
		logError(e, 'Could not load SCIM settings');
		dispatch({ type: LOAD_ERROR });
	}
};

/**
 * Saved immediately rather than behind a Save button. The role gates minting —
 * the credential is refused without one — so making it a separate deliberate
 * step would just be a second click before the button that matters.
 */
export const setDefaultRole = (defaultRoleId: number) => async (dispatch: any, getStore: any) => {
	const previous = getStore().ScimProvisioning.defaultRoleId;
	dispatch({ type: SET_DEFAULT_ROLE, defaultRoleId });
	try {
		await ClientParam.save({ id: SCIM_DEFAULT_ROLE_PARAM, value: defaultRoleId });
	} catch (e) {
		// Roll back, or the Generate button unlocks against a server that still
		// has no role and will reject the mint with a 400.
		dispatch({ type: SAVE_FAILED, defaultRoleId: previous });
		logError(e, 'Could not save the SCIM default role');
		NotificationService.add({
			style: NotificationService.style.ERROR,
			icon: 'times',
			title: 'admin.scim.title',
			body: 'default.unableToSave'
		});
	}
};

export const mint = () => async (dispatch: any) => {
	dispatch({ type: SAVING });
	try {
		const { key, baseUrl } = await ScimToken.mint();
		dispatch({ type: MINTED, key, baseUrl });
	} catch (e) {
		logError(e, 'Could not create the SCIM token');
		dispatch({ type: SAVE_FAILED });
		NotificationService.add({
			style: NotificationService.style.ERROR,
			icon: 'times',
			title: 'admin.scim.title',
			body: 'admin.scim.tokenError'
		});
	}
};

export const revoke = () => async (dispatch: any) => {
	dispatch({ type: SAVING });
	try {
		await ScimToken.revoke();
		dispatch({ type: REVOKED });
	} catch (e) {
		logError(e, 'Could not revoke the SCIM token');
		dispatch({ type: SAVE_FAILED });
		NotificationService.add({
			style: NotificationService.style.ERROR,
			icon: 'times',
			title: 'admin.scim.title',
			body: 'default.unableToSave'
		});
	}
};

export const dismissKey = () => ({ type: DISMISS_KEY });
